import React, { useState } from 'react';
import { ChevronDown, ChevronRight, BookOpen, FileText } from 'lucide-react';

// Helper to format cell text (Markdown-lite) - reused from parent
const formatCellContent = (text) => {
    if (typeof text !== 'string') {return text;}

    return text.split('\n').map((line, i) => {
        // Handle Headers (### Header)
        if (line.trim().startsWith('###')) {
            const content = line.trim().replace(/^###\s*/, '');
            return (
                <h3 key={i} className="text-lg font-black text-slate-800 uppercase tracking-tight mt-6 mb-3">
                    {content}
                </h3>
            );
        }

        // Handle Bold (**text**)
        const parts = line.split(/(\*\*.*?\*\*)/g);
        return (
            <div key={i} className={`min-h-[1.2em] ${line.trim().startsWith('-') ? 'pl-4' : ''}`}>
                {parts.map((part, j) => {
                    if (part.startsWith('**') && part.endsWith('**')) {
                        return <strong key={j} className="font-bold text-slate-800">{part.slice(2, -2)}</strong>;
                    }
                    return <span key={j}>{part}</span>;
                })}
            </div>
        );
    });
};

const CollapsiblePlaybookRow = ({ row, rowIndex }) => {
    const [bodyExpanded, setBodyExpanded] = useState(false);
    const [deepDiveExpanded, setDeepDiveExpanded] = useState(false);

    const hasBody = row.Body && row.Body.trim() !== '';
    const hasDeepDive = row.Native_Deep_Dive && row.Native_Deep_Dive.trim() !== '';

    return (
        <tr key={rowIndex} className="border-b-[16px] border-slate-100/30 last:border-0">
            <td colSpan="100%" className="p-0">
                <div className="bg-white group/row transition-all hover:bg-slate-50/10 shadow-sm hover:shadow-md">
                    {/* Title Row with Numbering */}
                    <div className="px-8 py-7 flex items-start gap-6">
                        {/* Lesson Number Badge */}
                        <div className="flex-shrink-0 w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/20 group-hover/row:scale-110 transition-transform duration-500">
                            <span className="text-white font-black text-lg">{rowIndex + 1}</span>
                        </div>

                        <div className="flex-1">
                            <div className="flex items-center gap-3 mb-1">
                                <span className="text-[10px] font-black text-blue-600 uppercase tracking-[0.2em] bg-blue-50 px-2 py-0.5 rounded-md">
                                    {row.Content_ID || 'Lesson'}
                                </span>
                                {hasDeepDive && (
                                    <span className="text-[10px] font-black text-emerald-600 uppercase tracking-[0.2em] bg-emerald-50 px-2 py-0.5 rounded-md">
                                        Deep Dive Available
                                    </span>
                                )}
                            </div>
                            <h3 className="text-xl font-extrabold text-slate-800 tracking-tight leading-tight">
                                {row.Title || 'Step into the Platform'}
                            </h3>
                            {row.Module && (
                                <p className="text-[11px] text-slate-400 font-bold uppercase tracking-widest mt-2 flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 bg-slate-300 rounded-full"></span>
                                    {row.Module}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Content Sections */}
                    <div className="px-8 pb-8 space-y-4">
                        {/* Lesson Content Button */}
                        {hasBody && (
                            <div className={`rounded-3xl border-2 transition-all duration-500 overflow-hidden ${bodyExpanded ? 'border-blue-200 bg-blue-50/30' : 'border-slate-100 hover:border-blue-100 bg-white'}`}>
                                <button
                                    onClick={() => setBodyExpanded(!bodyExpanded)}
                                    className="w-full px-6 py-5 flex items-center justify-between gap-3 text-left group"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={`p-2.5 rounded-xl transition-colors ${bodyExpanded ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-400 group-hover:bg-blue-100 group-hover:text-blue-600'}`}>
                                            <FileText size={20} />
                                        </div>
                                        <div>
                                            <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest block mb-0.5">Section 01</span>
                                            <span className="text-sm font-black text-slate-700 uppercase tracking-tight">
                                                Core Lesson Content
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className={`text-[10px] font-black uppercase tracking-widest transition-opacity duration-300 ${bodyExpanded ? 'text-blue-600' : 'text-slate-400 opacity-0 group-hover:opacity-100'}`}>
                                            {bodyExpanded ? 'Close' : 'Read Now'}
                                        </span>
                                        <div className={`p-1.5 rounded-full transition-all duration-500 ${bodyExpanded ? 'bg-blue-600 text-white rotate-180' : 'bg-slate-100 text-slate-400'}`}>
                                            <ChevronDown size={16} />
                                        </div>
                                    </div>
                                </button>

                                {bodyExpanded && (
                                    <div className="px-8 py-8 bg-white/50 border-t border-blue-100 animate-in slide-in-from-top-4 duration-500">
                                        <div className="prose prose-blue max-w-none text-slate-600 leading-relaxed font-medium">
                                            {formatCellContent(row.Body)}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Deep Dive Section */}
                        {hasDeepDive && (
                            <div className={`rounded-3xl border-2 transition-all duration-500 overflow-hidden ${deepDiveExpanded ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-100 hover:border-emerald-100 bg-white'}`}>
                                <button
                                    onClick={() => setDeepDiveExpanded(!deepDiveExpanded)}
                                    className="w-full px-6 py-5 flex items-center justify-between gap-3 text-left group"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={`p-2.5 rounded-xl transition-colors ${deepDiveExpanded ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-400 group-hover:bg-emerald-100 group-hover:text-emerald-600'}`}>
                                            <BookOpen size={20} />
                                        </div>
                                        <div>
                                            <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest block mb-0.5">Section 02</span>
                                            <span className="text-sm font-black text-slate-700 uppercase tracking-tight">
                                                Advanced Deep Dive
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className={`text-[10px] font-black uppercase tracking-widest transition-opacity duration-300 ${deepDiveExpanded ? 'text-emerald-600' : 'text-slate-400 opacity-0 group-hover:opacity-100'}`}>
                                            {deepDiveExpanded ? 'Close' : 'Explore'}
                                        </span>
                                        <div className={`p-1.5 rounded-full transition-all duration-500 ${deepDiveExpanded ? 'bg-emerald-600 text-white rotate-180' : 'bg-slate-100 text-slate-400'}`}>
                                            <ChevronDown size={16} />
                                        </div>
                                    </div>
                                </button>

                                {deepDiveExpanded && (
                                    <div className="px-8 py-8 bg-white/50 border-t border-emerald-100 animate-in slide-in-from-top-4 duration-500">
                                        <div className="prose prose-emerald max-w-none text-slate-600 leading-relaxed font-medium">
                                            {formatCellContent(row.Native_Deep_Dive)}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </td>
        </tr>
    );
};

export default CollapsiblePlaybookRow;
